/**
 * The Preview tab of the prompt box (docs/refactor/11-preview.md): what is running on this machine,
 * beside the conversation. Reload, open in the browser, a phone's or a tablet's width, and "Mostrar
 * ao agente" — which puts the address in the prompt box, ready for what to say about it.
 */

import { useState } from 'react'
import { ExternalLink, MessageSquareText, RotateCw, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { openExternal } from '../lib/desktop'
import { askAboutPreview, closePreview, PREVIEW_SIZES, type PreviewSize } from '../lib/preview'
import { COMPOSE_EVENT } from '../lib/dockPlacement'

export function PreviewPanel({ url }: { url: string }) {
  const { t, locale } = useI18n()
  const [size, setSize] = useState<PreviewSize>('full')
  // A new key reloads the frame: the page is another origin, so it cannot be told to reload itself.
  const [nonce, setNonce] = useState(0)
  const width = PREVIEW_SIZES[size]

  return (
    <div className="preview">
      <div className="preview__bar">
        <span className="mono tiny truncate preview__url" title={url}>{url}</span>
        <div className="segmented" role="radiogroup" aria-label={t('preview.size')}>
          {(Object.keys(PREVIEW_SIZES) as PreviewSize[]).map((key) => (
            <button key={key} type="button" role="radio" aria-checked={size === key} className={`segmented__item${size === key ? ' segmented__item--active' : ''}`} onClick={() => setSize(key)}>
              {t(`preview.size.${key}`)}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => setNonce(nonce + 1)} title={t('preview.reload')} aria-label={t('preview.reload')}>
          <RotateCw size={13} />
        </button>
        <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={() => void openExternal(url)} title={t('preview.openBrowser')} aria-label={t('preview.openBrowser')}>
          <ExternalLink size={13} />
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => window.dispatchEvent(new CustomEvent(COMPOSE_EVENT, { detail: askAboutPreview(url, size, locale) }))}
          title={t('preview.showAgentHint')}
        >
          <MessageSquareText size={13} /> {t('preview.showAgent')}
        </button>
        <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={closePreview} title={t('preview.close')} aria-label={t('preview.close')}>
          <X size={13} />
        </button>
      </div>
      <div className="preview__stage">
        <iframe
          key={`${url}#${nonce}`}
          src={url}
          title={t('preview.title')}
          className="preview__frame"
          style={width ? { width, maxWidth: '100%' } : undefined}
          // The project's own page: it may run its scripts and forms, nothing of the app's.
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
        />
      </div>
      <p className="tiny muted preview__note">{t('preview.note')}</p>
    </div>
  )
}
